import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../contexts/CurrencyContext';
import { useCart } from '../contexts/CartContext';
import './ItemCard.css';

export interface PricingTier {
  storeItemPricingTierId: number;
  storeItemId: number;
  minQuantity: number;
  maxQuantity: number | null;
  unitPrice: number;
  isActive: boolean;
  displayOrder: number;
}

export interface Item {
  id: number;
  name: string;
  nameEn?: string;
  price: number;
  image: string;
  description: string;
  category: string;
  categoryId?: number;
  categoryName?: string;
  stockStatus: 'inStock' | 'limitedStock' | 'outOfStock';
  deliveryDays: number;
  paymentOption: 'full' | 'partial';
  minimumOrderQuantity?: number;
  videoUrl?: string;
  isExternalOrder?: boolean;
  images?: Array<{
    imageUrl?: string;
    imagePath?: string;
    isDefault?: boolean;
  }>;
  pricingTiers?: PricingTier[];
  shippingDetails?: Array<{
    storeShippingMethodId: number;
    methodNameAr: string;
    methodNameEn: string;
    price: number;
    minDays: number;
    maxDays: number;
  }>;
}

interface ItemCardProps {
  item: Item;
}

const ItemCard: React.FC<ItemCardProps> = ({ item }) => {
  
  const { formatPrice } = useCurrency();
  const { addToCart } = useCart();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const displayName = i18n.language === 'en' && item.nameEn ? item.nameEn : item.name;

  const [showWholesaleModal, setShowWholesaleModal] = useState(false);
  const [quantity, setQuantity] = useState<number>(item.minimumOrderQuantity || 1);
  const [calculatedPrice, setCalculatedPrice] = useState<number>(item.price);
  const [selectedTier, setSelectedTier] = useState<PricingTier | null>(null);
  const [quantityError, setQuantityError] = useState<string>('');

  // Calculate price based on quantity and pricing tiers
  const calculatePriceForQuantity = (qty: number): { price: number; tier: PricingTier | null } => {
    if (!item.pricingTiers || item.pricingTiers.length === 0) {
      return { price: item.price, tier: null };
    }

    const sortedTiers = [...item.pricingTiers].sort(
      (a, b) => a.displayOrder - b.displayOrder || a.minQuantity - b.minQuantity
    );

    const applicableTier = sortedTiers.find(tier => {
      const minQty = tier.minQuantity;
      const maxQty = tier.maxQuantity ?? Infinity;
      return qty >= minQty && qty <= maxQty;
    });

    return {
      price: applicableTier?.unitPrice || item.price,
      tier: applicableTier || null
    };
  };

  // Update calculated price when quantity changes
  useEffect(() => {
    const minQty = item.minimumOrderQuantity || 1;
    const validQty = Math.max(minQty, quantity);
    const { price, tier } = calculatePriceForQuantity(validQty);
    setCalculatedPrice(price);
    setSelectedTier(tier);
    if (quantity < minQty) {
      setQuantity(minQty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity, item.pricingTiers, item.price, item.minimumOrderQuantity]);

  const handleAddToCart = () => {
    const minQty = item.minimumOrderQuantity || 1;
    
    // If has pricing tiers, open wholesale modal instead
    if (item.pricingTiers && item.pricingTiers.length > 0) {
      setQuantity(minQty);
      setShowWholesaleModal(true);
    } else {
      // No tiers - add minimum quantity with base price
      addToCart({
        id: item.id,
        name: item.name,
        nameEn: item.nameEn,
        price: item.price,
        image: item.image,
        pricingTiers: item.pricingTiers,
        minimumOrderQuantity: item.minimumOrderQuantity,
        shippingDetails: item.shippingDetails,
        isExternalOrder: item.isExternalOrder
      }, minQty);
    }
  };

  const handleWholesaleAddToCart = () => {
    const minQty = item.minimumOrderQuantity || 1;
    const validQty = Math.max(minQty, quantity);
    // Let CartContext handle the price calculation based on tiers
    
    addToCart({
      id: item.id,
      name: item.name,
      nameEn: item.nameEn,
      price: item.price, // Pass base price
      originalPrice: item.price, // Explicitly pass base price as original
      image: item.image,
      pricingTiers: item.pricingTiers,
      minimumOrderQuantity: item.minimumOrderQuantity,
      shippingDetails: item.shippingDetails,
      isExternalOrder: item.isExternalOrder
    }, validQty);
    
    setShowWholesaleModal(false);
    setQuantity(item.minimumOrderQuantity || 1);
  };

  const handleQuantityChange = (value: number) => {
    const minQty = item.minimumOrderQuantity || 1;
    
    // Auto-correct if below minimum
    if (value < minQty) {
      setQuantity(minQty);
      setQuantityError(t('minOrderQuantity', { count: minQty }) as string);
      setTimeout(() => setQuantityError(''), 3000);
    } else {
      setQuantity(value);
      setQuantityError('');
    }
  };

  // Calculate savings
  const calculateSavings = () => {
    const baseTotalPrice = item.price * quantity;
    const tierTotalPrice = calculatedPrice * quantity;
    const savings = baseTotalPrice - tierTotalPrice;
    const savingsPercentage = (savings / baseTotalPrice) * 100;
    return { savings, savingsPercentage };
  };

  const { savings, savingsPercentage } = calculateSavings();

  const sortedTiers = item.pricingTiers 
    ? [...item.pricingTiers].sort(
        (a, b) => a.displayOrder - b.displayOrder || a.minQuantity - b.minQuantity
      )
    : [];

  return (
    <Card className="item-card-mic">
      <div className="item-image-wrapper">
        <Card.Img 
          variant="top" 
          src={item.image} 
          className="item-image-mic"
          alt={displayName}
        />
        {item.stockStatus === 'outOfStock' && (
          <div className="out-of-stock-overlay">
            <span>{t('outOfStock')}</span>
          </div>
        )}
        {item.stockStatus === 'limitedStock' && (
          <div className="limited-stock-badge">
            <i className="fas fa-exclamation-triangle"></i>
            {t('limitedStock')}
          </div>
        )}
        <div className="item-actions-overlay">
          <Link 
            to={`/item/${item.id}`}
            className="quick-view-btn"
          >
            <i className="fas fa-eye"></i>
          </Link>
          <Button 
            variant="light" 
            size="sm"
            className="quick-view-btn"
            onClick={handleAddToCart}
            disabled={item.stockStatus === 'outOfStock'}
          >
            <i className="fas fa-shopping-cart"></i>
          </Button>
        </div>
      </div>
      <Card.Body className="item-card-body">
           <Link 
            to={`/item/${item.id}`}>
          
        <Card.Title className="item-title-mic" title={displayName}>
          {displayName.length > 50 ? `${displayName.substring(0, 50)}...` : displayName}
        </Card.Title>
        </Link>
        <div className="item-price-section">
          <div className="item-price-section-top">
          <span className="item-price-mic">{formatPrice(item.price)}</span>
          {item.paymentOption === 'partial' && (
            <span className="payment-option-badge">
              <i className="fas fa-credit-card"></i>
              {t('partialPayment')}
            </span>
          )}
          </div>
          <div className="price-tiers-container">
            {item.pricingTiers && item.pricingTiers.length > 0 ? (
              <div>
                <i className="fas fa-tags"></i>
                <span>{t('quantityPricing')}</span>
                <div className="item-info-row">
                  {[...item.pricingTiers]
                    .sort((a, b) => a.displayOrder - b.displayOrder || a.minQuantity - b.minQuantity)
                    .map((tier, index) => (
                      <div key={tier.storeItemPricingTierId} className={`item-info-item ${index === 0 ? 'default-price' : ''}`}>
                        <span className="">
                          {tier.minQuantity.toFixed(0)} - {tier.maxQuantity ? tier.maxQuantity.toFixed(0) : t('more')}
                        </span>
                        <span className="">{formatPrice(tier.unitPrice)}</span>
                      </div>
                    ))}
                </div>
              </div>
            ) : (
              <div className="price-tiers-placeholder"></div>
            )}
          </div>
        </div>

        <div className="item-info-row">
          <div className="item-info-item">
            <i className="fas fa-truck"></i>
            <span>{item.deliveryDays} day</span>
          </div>
          {item.minimumOrderQuantity && item.minimumOrderQuantity > 0 && (
            <div className="item-info-item">
              <i className="fas fa-shopping-bag"></i>
              <span>Min Order: {item.minimumOrderQuantity}</span>
            </div>
          )}
          {item.isExternalOrder && (
            <div className="item-info-item">
              <i className="fas fa-clock"></i>
              <span>External Order</span>
            </div>
          )}
          {item.videoUrl && (
            <div className="item-info-item">
              <i className="fas fa-video"></i>
              <span>Video</span>
            </div>
          )}
        </div>

        <div className="item-actions">
          {item.pricingTiers && item.pricingTiers.length > 0 ? (
            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                className="add-to-cart-btn-alibaba"
                onClick={handleAddToCart}
                disabled={item.stockStatus === 'outOfStock'}
                size="sm"
              >
                {item.stockStatus === 'outOfStock' ? (
                  <>
                    <i className="fas fa-ban me-1"></i>
                    {t('notAvailable')}
                  </>
                ) : (
                  <>
                    <i className="fas fa-shopping-cart me-1"></i>
                    {t('addToCart')}
                  </>
                )}
              </Button>
           
            </div>
          ) : (
          <Button 
            variant="primary" 
            className="add-to-cart-btn-alibaba"
            onClick={handleAddToCart}
            disabled={item.stockStatus === 'outOfStock'}
            style={{ width: '100%' }}
          >
            {item.stockStatus === 'outOfStock' ? (
              <>
                <i className="fas fa-ban me-1"></i>
                {t('notAvailable')}
              </>
            ) : (
              <>
                <i className="fas fa-shopping-cart me-1"></i>
                {t('addToCart')}
              </>
            )}
          </Button>
          )}
        </div>
      </Card.Body>

      {/* Wholesale Purchase Modal */}
      <Modal 
        show={showWholesaleModal} 
        onHide={() => {
          setShowWholesaleModal(false);
          setQuantity(item.minimumOrderQuantity || 1);
        }}
        centered
        dir={isRTL ? 'rtl' : 'ltr'}
        size="lg"
        backdrop="static"
      >
        <Modal.Header>
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => {
              setShowWholesaleModal(false);
              setQuantity(item.minimumOrderQuantity || 1);
            }}
            aria-label="Close"
          ></button>
          <Modal.Title>
            <i className="fas fa-crown me-2" style={{ color: '#fbbf24' }}></i>
            {t('wholesale')} - {displayName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sortedTiers.length > 0 && (
            <div className="wholesale-pricing-tiers mb-4">
              <div style={{ marginBottom: '16px' }}>
                <h6 className="mb-0">
                  <i className="fas fa-list me-2"></i>
                  {t('chooseQuantityMsg')}
                </h6>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                  {t('clickRowMsg')}
                </p>
              </div>
              <div className="table-responsive">
                <table className="table table-hover table-sm">
                  <thead>
                    <tr>
                      <th>🎯 {t('targetQuantity')}</th>
                      <th>💰 {t('unitPrice')}</th>
                      <th>✨ {t('totalPrice')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTiers.map((tier) => {
                      const isSelected = selectedTier?.storeItemPricingTierId === tier.storeItemPricingTierId;
                      const exampleQuantity = tier.minQuantity;
                      const totalPrice = tier.unitPrice * exampleQuantity;
                      const discount = ((item.price - tier.unitPrice) / item.price) * 100;
                      
                      return (
                        <tr 
                          key={tier.storeItemPricingTierId}
                          className={isSelected ? 'table-success tier-selected' : 'tier-row'}
                          style={{ 
                            cursor: 'pointer',
                            backgroundColor: isSelected ? '#d4edda' : 'transparent',
                            borderLeft: isSelected ? '4px solid #28a745' : '4px solid transparent'
                          }}
                          onClick={() => {
                            setQuantity(tier.minQuantity);
                          }}
                        >
                          <td className="fw-bold">
                            {tier.minQuantity.toFixed(0)} 
                            {tier.maxQuantity ? ` - ${tier.maxQuantity.toFixed(0)}` : ` - ${t('andMore')}`}
                          </td>
                          <td className="fw-bold">{formatPrice(tier.unitPrice)}</td>
                          <td>
                            <div className="fw-bold text-success">
                              {formatPrice(totalPrice)}
                            </div>
                            <small className="text-muted">({tier.minQuantity.toFixed(0)} {t('unit')})</small>
                            {discount > 0 && (
                              <div className="badge bg-success ms-2">
                                {discount.toFixed(0)}% {t('discount')}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>
              <i className="fas fa-plus-circle me-2" style={{ color: '#059669' }}></i>
              {t('chooseQuantity')}
            </Form.Label>
            <InputGroup className="input-group-lg">
              <Button 
                variant="outline-secondary"
                onClick={() => {
                  const minQty = item.minimumOrderQuantity || 1;
                  if (quantity > minQty) {
                    handleQuantityChange(quantity - 1);
                  }
                }}
                disabled={quantity <= (item.minimumOrderQuantity || 1)}
              >
                <i className="fas fa-minus"></i>
              </Button>
              <Form.Control
                type="number"
                value={quantity}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || (item.minimumOrderQuantity || 1);
                  handleQuantityChange(value);
                }}
                onKeyDown={(e) => {
                  // Arrow keys for increment/decrement
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    handleQuantityChange(quantity + 1);
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    const minQty = item.minimumOrderQuantity || 1;
                    if (quantity > minQty) {
                      handleQuantityChange(quantity - 1);
                    }
                  }
                }}
                min={item.minimumOrderQuantity || 1}
                step="1"
                className="text-center"
                isInvalid={!!quantityError}
              />
              <Button 
                variant="outline-secondary"
                onClick={() => handleQuantityChange(quantity + 1)}
              >
                <i className="fas fa-plus"></i>
              </Button>
            </InputGroup>
            {quantityError && (
              <Form.Text className="text-warning d-block mt-2">
                <i className="fas fa-exclamation-circle me-1"></i>
                {quantityError}
              </Form.Text>
            )}
            {item.minimumOrderQuantity && item.minimumOrderQuantity > 1 && !quantityError && (
              <Form.Text className="text-muted">
                <i className="fas fa-info-circle me-1"></i>
                {t('minOrderQuantity', { count: item.minimumOrderQuantity })}
              </Form.Text>
            )}
          </Form.Group>

          <div className="price-summary p-3 bg-light rounded">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold">
                <i className="fas fa-tag me-2"></i>
                {t('unitPrice')}
              </span>
              <div>
                {item.price !== calculatedPrice && (
                  <span className="text-muted text-decoration-line-through me-2" style={{ fontSize: '0.9rem' }}>
                    {formatPrice(item.price)}
                  </span>
                )}
                <span className="fw-bold text-success fs-5">
                  {formatPrice(calculatedPrice)}
                </span>
              </div>
            </div>
            
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="fw-bold">
                <i className="fas fa-receipt me-2"></i>
                {t('totalPrice')}
              </span>
              <span className="fw-bold text-primary fs-4">
                {formatPrice(calculatedPrice * quantity)}
              </span>
            </div>

            {savings > 0 && (
              <div className="alert alert-success py-2 mb-2" role="alert">
                <i className="fas fa-star me-2"></i>
                <span className="fw-bold">{t('savings')} {savingsPercentage.toFixed(1)}%</span>
                <span className="d-block small mt-1">{formatPrice(savings)} {t('discount')}</span>
              </div>
            )}

            {selectedTier && (
              <div className="alert alert-info py-2">
                <i className="fas fa-bolt me-2"></i>
                <strong>{t('specialPrices')}</strong>
                <br />
                <small>{selectedTier.minQuantity.toFixed(0)} 
                {selectedTier.maxQuantity ? ` - ${selectedTier.maxQuantity.toFixed(0)}` : ` - ${t('andMore')}`} {t('unit')}</small>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowWholesaleModal(false);
              setQuantity(item.minimumOrderQuantity || 1);
            }}
          >
            <i className="fas fa-times me-2"></i>
            {t('cancel')}
          </Button>
          <Button 
            variant="success" 
            onClick={handleWholesaleAddToCart}
            disabled={item.stockStatus === 'outOfStock'}
            className="fw-bold"
          >
            <i className="fas fa-shopping-cart me-2"></i>
            {t('addToCartWithQty', { count: quantity })}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default ItemCard;
